import { useState, useMemo, useRef, useEffect } from "react";
import ExerciseDetailModal from "../components/ExerciseDetailModal";
import { fmtWeight, wtLabel, normalize } from "../helpers";

function avgDifficulty(sets) {
  if (!sets.length) return 0;
  const map = { Low: 1, Medium: 2, High: 3 };
  return sets.reduce((a, s) => a + (map[s.difficulty] || 2), 0) / sets.length;
}

export default function ExercisesView({ exercises, onEdit, onDelete, onDeleteSet, units = "lbs" }) {
  const [search, setSearch]             = useState("");
  const [muscleFilter, setMuscleFilter] = useState("all");
  const [selected, setSelected]         = useState(null);
  const [filterOpen, setFilterOpen]     = useState(false);
  const filterRef                       = useRef();

  useEffect(() => {
    const fn = e => { if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const muscles = useMemo(
    () => [...new Set(exercises.flatMap(e => e.muscles?.length ? e.muscles : [e.muscle]).filter(Boolean))].sort(),
    [exercises]
  );

  const filtered = useMemo(() => exercises.filter(ex => {
    const mMatch = muscleFilter === "all" || ex.muscles?.includes(muscleFilter) || ex.muscle === muscleFilter;
    const sMatch = normalize(ex.name).includes(normalize(search));
    return mMatch && sMatch;
  }).sort((a, b) => a.name.localeCompare(b.name)), [exercises, muscleFilter, search]);

  return (
    <div className="fade-in">

      {/* ── SEARCH + FILTER ── */}
      <section style={{ display: "flex", gap: 8, marginBottom: 24, alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1 }}>
          <div style={{ position: "absolute", top: "50%", left: 12, transform: "translateY(-50%)", color: "#8e9197", display: "flex", pointerEvents: "none" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="SEARCH..."
            style={{
              width: "100%", background: "var(--bg-lowest)",
              border: "none", borderRadius: 8,
              padding: "12px 12px 12px 36px",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 11, fontWeight: 700, letterSpacing: "0.15em",
              color: "var(--text)", outline: "none", boxSizing: "border-box",
            }}
          />
        </div>
        <div ref={filterRef} style={{ position: "relative", flexShrink: 0 }}>
          <button
            onClick={() => setFilterOpen(p => !p)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: muscleFilter !== "all" ? "#94a3b8" : "var(--bg-low)",
              color: muscleFilter !== "all" ? "#233143" : "#94a3b8",
              border: "1px solid rgba(68,71,76,0.2)",
              borderRadius: 8, padding: "12px 12px",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
              cursor: "pointer", outline: "none", whiteSpace: "nowrap",
              textTransform: "uppercase",
            }}
          >
            {muscleFilter === "all" ? "ALL" : muscleFilter.toUpperCase()}
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.6, transform: filterOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
              <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {filterOpen && (
            <div style={{
              position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 100,
              background: "var(--bg-low)", border: "1px solid rgba(68,71,76,0.25)",
              borderRadius: 10, overflow: "hidden",
              boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
              minWidth: 140,
            }}>
              {["all", ...muscles].map(m => (
                <div key={m} onClick={() => { setMuscleFilter(m); setFilterOpen(false); }} style={{
                  padding: "10px 14px",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
                  textTransform: "uppercase", cursor: "pointer",
                  color: muscleFilter === m ? "#b9c8de" : "#8e9197",
                  background: muscleFilter === m ? "rgba(185,200,222,0.08)" : "transparent",
                  borderBottom: "1px solid rgba(68,71,76,0.1)",
                }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(185,200,222,0.06)"}
                  onMouseLeave={e => e.currentTarget.style.background = muscleFilter === m ? "rgba(185,200,222,0.08)" : "transparent"}
                >
                  {m === "all" ? "ALL" : m}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── EXERCISE CARDS ── */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px" }}>
          <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "#64748b", letterSpacing: "0.15em" }}>
            {exercises.length === 0 ? "NO EXERCISES YET — TAP + TO ADD" : "NO RESULTS"}
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {filtered.map(ex => {
            const vol = ex.sets.reduce((a, s) => a + s.reps * s.weight, 0);
            const bestSet = ex.sets.reduce((b, s) => s.weight > (b?.weight||0) || (s.weight === (b?.weight||0) && s.reps > (b?.reps||0)) ? s : b, null);
            const avgDiff = avgDifficulty(ex.sets);
            const diffLabel = avgDiff >= 2.5 ? "HARD" : avgDiff >= 1.5 ? "MED" : ex.sets.length ? "EASY" : null;
            const diffColor = { HARD: "#e1c19d", MED: "#c2c7d0", EASY: "#b9c8de" };

            return (
              <div key={ex.id} onClick={() => setSelected(ex)} style={{
                position: "relative", background: "var(--bg-low)", borderRadius: 10,
                overflow: "hidden", padding: "12px 12px 0", textAlign: "center",
                borderLeft: "3px solid #b9c8de", cursor: "pointer",
                boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
              }}>
                {/* Name */}
                <h3 style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: 11, fontWeight: 700, letterSpacing: "-0.01em",
                  textTransform: "uppercase", color: "var(--text)", marginBottom: 3,
                  lineHeight: 1.3,
                }}>{ex.name}</h3>

                {/* Muscle group + difficulty inline */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  gap: 6, marginBottom: 8,
                }}>
                  <p style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 8, color: "#8e9197", letterSpacing: "0.2em",
                    textTransform: "uppercase", margin: 0,
                  }}>
                    {(ex.muscles?.length ? ex.muscles : [ex.muscle]).filter(Boolean).slice(0, 1).join(" | ")}
                  </p>
                  {diffLabel && (
                    <span style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: 8, fontWeight: 900, letterSpacing: "0.1em",
                      color: diffColor[diffLabel],
                      background: `${diffColor[diffLabel]}18`,
                      padding: "2px 5px", borderRadius: 4,
                    }}>{diffLabel}</span>
                  )}
                </div>

                {/* Stats row */}
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 4, paddingTop: 8, paddingBottom: 8,
                  borderTop: "1px solid rgba(68,71,76,0.12)",
                }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 700, color: "#b9c8de" }}>{ex.sets.length}</span>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 7, color: "#8e9197", letterSpacing: "0.1em" }}>SETS</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", borderLeft: "1px solid rgba(68,71,76,0.12)", borderRight: "1px solid rgba(68,71,76,0.12)" }}>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
                      {bestSet ? fmtWeight(bestSet.weight, units) : "—"}
                    </span>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 7, color: "#8e9197", letterSpacing: "0.1em" }}>BEST</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
                      {vol > 999 ? `${(vol/1000).toFixed(1)}K` : vol || "—"}
                    </span>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 7, color: "#8e9197", letterSpacing: "0.1em" }}>VOL</span>
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <ExerciseDetailModal
          exercise={selected}
          onClose={() => setSelected(null)}
          onEdit={ex => { setSelected(null); onEdit(ex); }}
          onDelete={id => { setSelected(null); onDelete(id); }}
          onDeleteSet={setId => {
            onDeleteSet(setId, selected.id);
            setSelected(prev => prev ? { ...prev, sets: prev.sets.filter(s => s.id !== setId) } : null);
          }}
        />
      )}
    </div>
  );
}
